import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface Notification {
  id: string;
  user_id: string;
  type: string;
  title: string;
  body: string | null;
  metadata: Record<string, any>;
  is_read: boolean;
  created_at: string;
}

export function useNotifications() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: notifications = [], isLoading } = useQuery<Notification[]>({
    queryKey: ['notifications', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('notifications')
        .select('id, user_id, type, title, body, metadata, is_read, created_at')
        .order('created_at', { ascending: false })
        .limit(100); // Reduced from 200 to 100
      if (error) throw error;
      return (data || []) as unknown as Notification[];
    },
    enabled: !!user,
    staleTime: 60_000, // 1 minute
  });

  const unreadCount = notifications.filter(n => !n.is_read).length;

  // Realtime subscription - scoped to current user
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel('user-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['notifications', user.id] });
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user, queryClient]);

  const markAsRead = useCallback(async (id: string) => {
    await supabase.from('notifications').update({ is_read: true }).eq('id', id);
    queryClient.invalidateQueries({ queryKey: ['notifications', user?.id] });
  }, [user, queryClient]);

  const markAllAsRead = useCallback(async () => {
    const unread = notifications.filter(n => !n.is_read).map(n => n.id);
    if (unread.length === 0) return;
    await supabase.from('notifications').update({ is_read: true }).in('id', unread);
    queryClient.invalidateQueries({ queryKey: ['notifications', user?.id] });
  }, [notifications, user, queryClient]);

  const deleteNotification = useCallback(async (id: string) => {
    await supabase.from('notifications').delete().eq('id', id);
    queryClient.invalidateQueries({ queryKey: ['notifications', user?.id] });
  }, [user, queryClient]);

  const sendCustomNotification = useCallback(async (targetUserIds: string[], title: string, body: string, notificationType: string = 'info') => {
    const rows = targetUserIds.map(uid => ({
      user_id: uid,
      type: 'custom',
      title,
      body,
      metadata: { sent_by: user?.id, sent_by_name: user?.name, notification_type: notificationType },
    }));
    const { error } = await supabase.from('notifications').insert(rows);
    if (error) throw error;
  }, [user]);

  return {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    sendCustomNotification,
  };
}
